<template>
  <div id="app" class="d-flex flex-column min-vh-100">
    <!-- Navbar -->
    <Navbar @toggle-sidebar="toggleSidebar" :sidebarOpen="sidebarOpen" />

    <!-- Sidebar -->
    <Sidebar :isOpen="sidebarOpen" @close-sidebar="closeSidebar" />

    <!-- Overlay for mobile -->
    <div
      v-if="sidebarOpen"
      class="overlay d-lg-none"
      @click="closeSidebar"
    ></div>

    <!-- Hero Section (Home Page) -->
    <section
      v-if="$route.name === 'Home'"
      class="hero-section text-center text-white flex-shrink-0"
    >
      <div class="hero-overlay">
        <div class="hero-content">
          <h1 class="display-4 fw-bold">🙏 Welcome to {{ templeName }}</h1>
          <p class="lead">
            A sacred place of peace, mindfulness, and compassion.
          </p>
          <router-link to="/about" class="btn btn-light btn-lg mt-3">
            Learn More
          </router-link>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main
      :class="[
        'content flex-grow-1',
        { 'content-shift': sidebarOpen && isDesktop },
      ]"
    >
      <router-view />
    </main>

    <!-- Footer -->
    <footer class="temple-footer text-center text-white py-4 mt-auto">
      <p class="mb-1">
        © {{ new Date().getFullYear() }} {{ templeName }} | All Rights Reserved
      </p>

      <div class="d-flex justify-content-center align-items-center mt-2">
        <small class="me-2">Powered by</small>
        <a
          href="https://mysoftcode.com"
          target="_blank"
          rel="noopener noreferrer"
          class="d-flex align-items-center company-link"
        >
          <img
            :src="companyLogo"
            alt="MySoftCode Logo"
            class="company-logo me-1"
          />
          <strong>MySoftCode</strong>
        </a>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import Navbar from "@/components/NavbarView.vue";
import Sidebar from "@/components/SidebarView.vue";
import companyLogo from "@/assets/mysoftcode-logo.png"; // ✅ Add your logo image in src/assets

const sidebarOpen = ref(false);
const isDesktop = ref(window.innerWidth >= 992);
const templeName = ref("Sri Sambuddha Viharaya");

const toggleSidebar = () => (sidebarOpen.value = !sidebarOpen.value);
const closeSidebar = () => (sidebarOpen.value = false);

const handleResize = () => {
  isDesktop.value = window.innerWidth >= 992;
  if (!isDesktop.value) sidebarOpen.value = false;
};

onMounted(() => window.addEventListener("resize", handleResize));
onUnmounted(() => window.removeEventListener("resize", handleResize));
</script>

<style scoped>
#app {
  font-family: "Poppins", "Avenir", Helvetica, Arial, sans-serif;
  background-color: #fffdf8;
  color: #4a4a4a;
}

/* HERO SECTION */
.hero-section {
  position: relative;
  background: url("@/assets/temple-bg.jpg") center/cover no-repeat;
  height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hero-overlay {
  background-color: rgba(0, 0, 0, 0.55);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hero-content {
  z-index: 2;
}

/* MAIN CONTENT */
.content {
  transition: margin-left 0.3s ease;
  margin-left: 0;
  padding: 40px 20px;
}
.content-shift {
  margin-left: 250px;
}

/* OVERLAY FOR MOBILE */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1039;
}

/* FOOTER */
.temple-footer {
  background-color: #c69f59;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
}
.company-logo {
  height: 26px;
  width: auto;
  transition: transform 0.3s ease;
}
.company-link:hover .company-logo {
  transform: scale(1.1);
}
.company-link {
  text-decoration: none;
  color: #fff;
}
.company-link strong {
  font-weight: 600;
  color: #fff;
}
.btn-light {
  background-color: #fffbe8;
  color: #6b4e1e;
  border: none;
}
.btn-light:hover {
  background-color: #fff3c4;
  color: #4a3200;
}
</style>
